import { useMemo } from 'react';
import { useFamilyDescriptions } from './useFamilyDescriptions';

/** One description reduced to what the collision rule needs. */
export interface DescriptionNumberRow {
    id: string;
    findingId: string;
    /** The Planum-/Profilnummer. */
    number: string;
    /** The Teil mark; empty for an undivided description. */
    partLabel: string;
}

/** Trimmed, case-insensitive form a number is compared by. */
function numberKey(value: string): string {
    return value.trim().toLowerCase();
}

/** The slot a description occupies: its number, and which piece of it. */
export function slotKey(number: string, partLabel: string): string {
    return `${numberKey(number)}|${numberKey(partLabel)}`;
}

/**
 * The slots (normalized) that are described more than once — for callers that
 * already hold exactly one Befund's descriptions of one kind. Blank numbers
 * never count as a collision.
 *
 * A slot is the number PLUS its Teil: "Planum 1, Teil A" and "Planum 1, Teil B"
 * do not collide. What collides beyond the plain repeat is a number described
 * BOTH as a whole and in pieces; every row of such a number is flagged, because
 * which of them is wrong is not for us to decide.
 */
export function duplicateSlotKeys(
    slots: readonly { number: string; partLabel: string }[]
): Set<string> {
    const partsByNumber = new Map<string, string[]>();
    for (const slot of slots) {
        const number = numberKey(slot.number);
        if (number === '') {
            continue;
        }
        const parts = partsByNumber.get(number);
        if (parts) {
            parts.push(numberKey(slot.partLabel));
        } else {
            partsByNumber.set(number, [numberKey(slot.partLabel)]);
        }
    }
    const twice = new Set<string>();
    for (const [number, parts] of partsByNumber) {
        if (parts.length < 2) {
            continue;
        }
        // Whole AND pieces: the number itself is the contradiction, so all of
        // its rows are in it.
        if (parts.some((part) => part === '')) {
            for (const part of parts) {
                twice.add(`${number}|${part}`);
            }
            continue;
        }
        const seen = new Set<string>();
        for (const part of parts) {
            if (seen.has(part)) {
                twice.add(`${number}|${part}`);
            } else {
                seen.add(part);
            }
        }
    }
    return twice;
}

/** True when this slot is one of the collisions `duplicateSlotKeys` found. */
export function isDuplicateSlot(
    keys: ReadonlySet<string>,
    number: string,
    partLabel: string
): boolean {
    return keys.has(slotKey(number, partLabel));
}

/**
 * Description-slot collisions WITHIN a Befund. A Planum-/Profilnummer is unique
 * only under its own Befund — Bef. 12 and Bef. 13 may each hold a "Profil 10",
 * but neither may hold it twice — so the rule groups by findingId first. Planum
 * and Profil rows are separate namespaces; pass one kind at a time. A number
 * described in Teile holds each Teil once (see {@link duplicateSlotKeys}).
 *
 * Same reasoning as {@link computeFindingDuplicates}: the form dialogs check
 * this on save but cannot see a row another device wrote offline. ALL members
 * of a collision are flagged — there is no canonical row — and the user
 * renumbers one.
 *
 * Returns a map from each colliding row's id to its (trimmed) number.
 */
export function computeDescriptionDuplicates(
    rows: readonly DescriptionNumberRow[]
): Map<string, string> {
    const byFinding = new Map<string, DescriptionNumberRow[]>();
    for (const row of rows) {
        if (!row.findingId || numberKey(row.number) === '') {
            continue;
        }
        const group = byFinding.get(row.findingId);
        if (group) {
            group.push(row);
        } else {
            byFinding.set(row.findingId, [row]);
        }
    }

    const duplicates = new Map<string, string>();
    for (const group of byFinding.values()) {
        const twice = duplicateSlotKeys(group);
        if (twice.size === 0) {
            continue;
        }
        for (const row of group) {
            if (isDuplicateSlot(twice, row.number, row.partLabel)) {
                duplicates.set(row.id, row.number.trim());
            }
        }
    }
    return duplicates;
}

/**
 * Map of colliding Planum-/Profil-id -> its number, across the project's
 * Teilmaßnahme family. Family-scoped like the uniqueness check in the form
 * dialogs: linked projects share their Befunde, so one Befund's descriptions
 * may sit in any of them. Both kinds share one map — the ids are uuids.
 */
export function useDescriptionDuplicates(
    projectId: string
): Map<string, string> {
    const { planums, profiles } = useFamilyDescriptions(projectId);

    return useMemo(
        () =>
            new Map([
                ...computeDescriptionDuplicates(
                    planums.map((planum) => ({
                        id: planum.id,
                        findingId: planum.findingId,
                        number: planum.planumNumber,
                        partLabel: planum.partLabel
                    }))
                ),
                ...computeDescriptionDuplicates(
                    profiles.map((profile) => ({
                        id: profile.id,
                        findingId: profile.findingId,
                        number: profile.profileNumber,
                        partLabel: profile.partLabel
                    }))
                )
            ]),
        [planums, profiles]
    );
}
